import { useTheme } from '@mui/material/styles';
import type { FormikHelpers } from 'formik';
import { FieldArray, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { Flags, Schema } from 'yup';
import * as Yup from 'yup';

import RecordAttachmentForm from '@components/Attachments/RecordAttachmentForm';
import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField, FormikTextField } from '@components/FormikFields';
import { RecordSubmitButton } from '@components/RecordSubmitButton';
import RelatedRecordsForm from '@components/RelatedRecordsTable/RelatedRecordsForm';
import RoundedButton from '@components/RoundedButton/index';

import {
  LAB_REPORT_NAME_MAP,
  LAB_REPORT_TYPES,
} from '@constants/option-types/lab';
import { LAB_RESULTS } from '@constants/routes';

import {
  useCreateLabDiagnosticReport,
  useGetLabDiagnosticReport,
  useUpdateLabDiagnosticReport,
} from '@hooks/api/diagnostic-report';
import {
  useCreateLabObservation,
  useUpdateLabObservation,
} from '@hooks/api/lab-result';
import { useCuration } from '@hooks/useCuration';
import useFhirRecordForm from '@hooks/useFhirRecordForm';
import { useFormat } from '@hooks/useFormat';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { HorizontalRule } from '@layouts/AuthLayout';
import { ColumnLayout } from '@layouts/DrilldownStyle';

import type {
  DiagnosticReportRecord,
  DiagnosticReportValues,
  FormValues,
} from '@models/diagnostic-report';
import { getDiagnosticReportTemplate } from '@models/fhir-templates/diagnostic-report';
import { getEncounterTemplate } from '@models/fhir-templates/encounter';
import { getLocationTemplate } from '@models/fhir-templates/location';
import { getLabObservationTemplate } from '@models/fhir-templates/observation';
import type { LabObservationValues } from '@models/lab-result';

import { formatFacility } from '@utils/format';
import { getRecordSourceForSubmit } from '@utils/getRecordSourceForSubmit';
import { isBooleanValue, isNumberValue } from '@utils/lab';
import { queuePromises } from '@utils/queuePromises';
import { stripEmptyStrings } from '@utils/strip';

import DrilldownWrapper from '../Common/DrilldownWrapper';
import LabObservationForm from './LabObservationForm';
import { Title } from './SingleLabDrilldown.style';

const messages = defineMessages({
  test: {
    id: 'labDrilldown.test',
    defaultMessage: 'Date Tested: {date}',
  },
  conclusion: {
    id: 'labDrilldown.conclusion',
    defaultMessage: 'Conclusion',
  },
  reported: {
    id: 'labDrilldown.reported',
    defaultMessage: 'Date Reported: {date}',
  },
  typeName: {
    id: 'labDrilldown.typeName',
    defaultMessage: 'Lab Report Type',
  },
  report: {
    id: 'labDrilldown.report',
    defaultMessage: 'Lab Report',
  },
  practitioner: {
    id: 'GLOBAL.practitioner',
    defaultMessage: 'Practitioner',
  },
  facility: {
    id: 'GLOBAL.facility',
    defaultMessage: 'Facility',
  },
  city: {
    id: 'GLOBAL.city',
    defaultMessage: 'City',
  },
  country: {
    id: 'GLOBAL.country',
    defaultMessage: 'Country',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  results: {
    id: 'labDrilldown.results',
    defaultMessage: 'Results',
  },
  noLabObservation: {
    id: 'labDrilldown.noLabObservation',
    defaultMessage:
      'Select a lab report type in order to add a lab observation',
  },
  addResult: {
    id: 'labDrilldown.addResult',
    defaultMessage: 'Add Result',
  },
  testName: {
    id: 'labDrilldown.testName',
    defaultMessage: 'Report Test Name',
  },
  labResultRequired: {
    id: 'labDrilldown.error.labResultRequired',
    defaultMessage:
      'Please add at least one test result, including its name, value and unit.',
  },
});

interface LabResultDrilldownProps {
  id?: string;
  /**
   * When true, suppresses the default redirect to the LAB_RESULTS route
   * after successfully creating a new lab result.
   * - Default: false (keeps existing behavior in normal routes)
   * - In auto-curation flow, this should be set to true.
   */
  disableNavigateOnCreate?: boolean;
  /**
   * Callback invoked after successful form submission.
   * Used by the curation flow to remove the form from the list.
   */
  onSubmit?: () => void;
  data?: DiagnosticReportRecord;
}

const LabResultDrilldown: React.FC<LabResultDrilldownProps> = ({
  id = '',
  data: curationData,
  disableNavigateOnCreate = false,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const { formatDateLabel } = useFormat();
  const { enqueueSnackbar } = useSnackbar();
  const {
    formikHelpers: { baseFhirValidationSchema, initialFhirFormValues },
    loading: recordStatusAndSourceLoading,
    handleRecordFilesSourcePublishing,
  } = useFhirRecordForm();
  const { palette } = useTheme();

  const {
    state: { activeProfile, isCurator },
  } = useSession();
  const [patient_id] = [activeProfile?.patient_id];
  const {
    state: { isCreating, isEditing },
    finishAddingRecord,
    startAddingRecord,
    stopCurating,
    stopEditingRecord,
  } = useCuration();

  React.useEffect(() => {
    if (id === 'add') {
      startAddingRecord();
    }
    return () => stopCurating();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { data: diagnosticReportData, loading: diagnosticReportLoading } =
    useGetLabDiagnosticReport({
      record_id: id,
    });
  const data = curationData ?? diagnosticReportData;

  const [createDiagnosticReport, createDiagnosticReportLoading] =
    useCreateLabDiagnosticReport();
  const [updateDiagnosticReport, updateDiagnosticReportLoading] =
    useUpdateLabDiagnosticReport();

  const [createLabObservation, createLabObservationLoading] =
    useCreateLabObservation();
  const [updateLabObservation, updateLabObservationLoading] =
    useUpdateLabObservation();

  const loading =
    diagnosticReportLoading ||
    updateDiagnosticReportLoading ||
    createDiagnosticReportLoading ||
    createLabObservationLoading ||
    updateLabObservationLoading ||
    recordStatusAndSourceLoading;

  const initialValues: FormValues = {
    ...initialFhirFormValues,
    // title: data?.reportTestName ?? '',
    city: data?.city ?? '',
    code: data?.vitallCode ?? '',
    country: data?.country ?? '',
    details: data?.details ?? '',
    facilityName: data?.facilityName ?? '',
    findings: data?.findings ?? '',
    history: data?.history ?? '',
    practitioner: data?.practitioner ?? '',
    recommendations: data?.recommendations ?? '',
    reportedDate: data?.reportedDate ?? '',
    selfReported: data?.selfReported ?? false,
    technique: data?.technique ?? '',
    testDate: data?.testDate ?? '',
    typeName: data?.typeName ?? '',
    results: (data?.results ?? []).map(
      ({ id, category, code, displayName, testDate, unit, value }) => ({
        id,
        category,
        code,
        displayName,
        testDate,
        unit,
        value,
      })
    ),
    associatedResources: [],
  };

  const labValidationSchema: Schema<Partial<DiagnosticReportValues>> =
    Yup.object().shape({
      // title: Yup.string(),
      city: Yup.string(),
      code: Yup.string().required(formatMessage(messages.required)),
      country: Yup.string(),
      details: Yup.string(),
      facilityName: Yup.string(),
      findings: Yup.string(),
      history: Yup.string(),
      practitioner: Yup.string(),
      recommendations: Yup.string(),
      reportedDate: Yup.string(),
      selfReported: Yup.boolean().required(formatMessage(messages.required)),
      technique: Yup.string(),
      testDate: Yup.string().required(formatMessage(messages.required)),
      typeName: Yup.string(),
      results: Yup.array().of(Yup.mixed<LabObservationValues>().required()),
      associatedResources: Yup.array().of(
        Yup.string().required(formatMessage(messages.required))
      ),
    });
  const validationSchema: Schema<
    Partial<FormValues>,
    any,
    any,
    Flags
  > = labValidationSchema.concat(baseFhirValidationSchema);

  async function handleSubmit(
    values: FormValues,
    { validateForm }: FormikHelpers<FormValues>
  ) {
    // TODO: Location
    if (!patient_id) return;

    values = stripEmptyStrings(values);
    await validateForm(stripEmptyStrings(values));
    const validResults = (values.results ?? []).filter((r) => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (r.code && r.value !== undefined && r.value !== null && r.value !== '')
        return true;
      return false;
    });
    if (!validResults.length) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.labResultRequired),
      });
      return;
    }
    values.results = validResults;
    let labReport: DiagnosticReportRecord | null | undefined;

    try {
      // Update or post lab observations
      const responses = await queuePromises(values.results, async (result) => {
        const observation = getLabObservationTemplate(patient_id, {
          ...result,
          testDate: result.testDate ?? values.testDate,
        });
        if (result.id) {
          const existingLab = data?.results?.find((r) => r.id === result.id);
          const stringValue = result.value.toString();
          const currentValue: boolean | number | string =
            // eslint-disable-next-line no-nested-ternary
            isNumberValue(stringValue)
              ? Number(result.value)
              : isBooleanValue(result.value)
                ? Boolean(stringValue === 'Positive' || stringValue === 'true')
                : stringValue;

          // Check if the lab observation was changed
          // If lab report's test date is changed, update labs' as well
          if (
            existingLab?.code !== result.code ||
            existingLab.unit !== result.unit ||
            existingLab.value !== currentValue ||
            existingLab.category !== result.category ||
            initialValues.testDate !== values.testDate
          ) {
            const { data: updateLabData } = await updateLabObservation({
              input: {
                id: result.id,
                observation,
              },
            });
            return updateLabData?.updateLaboratoryObservationRecord.id;
          }
          return Promise.resolve(result.id);
        }
        const { data: addLabData } = await createLabObservation({
          input: {
            ...observation,
          },
        });
        return addLabData?.postLaboratoryObservationRecord.id;
      });

      const labResultIds = responses
        .filter((id) => id)
        .map((id) => `Observation/${id}`);

      if (data?.id) {
        const locationId = data.encounter?.location?.id;
        const encounterId = data.encounter?.id;
        const { data: updatedLabReport } = await updateDiagnosticReport({
          input: {
            diagnostic_report_id: data.id,
            diagnosticReport: {
              ...data.getModelInput(values),
              ...(labResultIds.length > 0 && {
                result: labResultIds.map((reference) => ({
                  reference,
                })),
              }),
            },
            location: {
              ...(locationId && {
                id: locationId,
              }),
              ...getLocationTemplate({
                city: values.city,
                country: values.country,
                name: values.facilityName,
                region: null,
              }),
            },
            encounter: {
              ...(encounterId && {
                id: encounterId,
              }),
              ...getEncounterTemplate(
                patient_id,
                {
                  practitioner: values.practitioner,
                  complications: null,
                  diagnosis: null,
                  endDate: null,
                  erNotes: null,
                  followUp: null,
                  selfReported: false,
                  startDate: null,
                  title: values.title ?? '',
                  visitDurationDays: null,
                  visitDurationUnits: null,
                },
                'laboratory'
              ),
            },
          },
        });
        labReport = updatedLabReport;
      } else {
        const { data } = await createDiagnosticReport({
          input: {
            diagnosticReport: getDiagnosticReportTemplate(
              patient_id,
              values,
              'laboratory',
              labResultIds
            ),
            location: getLocationTemplate({
              city: values.city,
              country: values.country,
              name: values.facilityName,
              region: null,
            }),
            encounter: getEncounterTemplate(
              patient_id,
              {
                practitioner: values.practitioner,
                complications: null,
                diagnosis: null,
                endDate: null,
                erNotes: null,
                followUp: null,
                selfReported: false,
                startDate: null,
                title: values.title ?? '',
                visitDurationDays: null,
                visitDurationUnits: null,
              },
              'laboratory'
            ),
          },
        });
        labReport = data;
      }
    } catch (e) {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.error),
      });
    } finally {
      await handleRecordFilesSourcePublishing({
        resource: labReport?.getResourceId(),
        files: values.files,
        toPublish: values.toPublish,
        recordSource: getRecordSourceForSubmit({
          isCurator,
          isCreating,
          initialRecordSource: initialValues.recordSource,
          currentRecordSource: values.recordSource,
        }),
      });
      // Existing behavior preserved by default.
      // In auto-curation flow, disableNavigateOnCreate=true will skip this.
      if (labReport && isCreating && !disableNavigateOnCreate) {
        navigate(`${LAB_RESULTS}${labReport.id}`);
      }
      stopEditingRecord();
      finishAddingRecord();
      onSubmit?.();
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ setFieldValue, values }) => {
        const reportType =
          isCreating || isEditing
            ? `${values.code ? `: ${LAB_REPORT_NAME_MAP[values.code]}` : ''}`
            : `${data?.typeName ? `: ${data.typeName}` : ''}`;
        const title = `${formatMessage(messages.report)}${reportType}`;

        return (
          <Form>
            <DrilldownWrapper
              associatedRecords={data?.associatedRecords}
              backRoute={LAB_RESULTS}
              items={[
                {
                  editingComponent: (
                    <FormikTextField
                      isHeaderItem
                      name="practitioner"
                      placeholder={formatMessage(messages.practitioner)}
                    />
                  ),
                  title: data?.practitioner,
                  iconName: 'user-doctor',
                },
                {
                  editingComponent: (
                    <>
                      <FormikTextField
                        isHeaderItem
                        name="facilityName"
                        placeholder={formatMessage(messages.facility)}
                      />
                      <FormikTextField
                        isHeaderItem
                        name="city"
                        placeholder={formatMessage(messages.city)}
                      />
                      <FormikTextField
                        isHeaderItem
                        name="country"
                        placeholder={formatMessage(messages.country)}
                      />
                    </>
                  ),
                  title: formatFacility(
                    data?.facilityName,
                    data?.city,
                    undefined,
                    data?.country
                  ),
                  iconName: 'location-dot',
                },
              ]}
              loading={loading}
              notes={data?.recordNotes}
              resource={data}
              selfReported={data?.selfReported}
              subtitle={
                isCreating || isEditing ? (
                  <>
                    <FormikFhirDateField
                      formats={['datetime', 'date', 'year']}
                      label={formatMessage(messages.test)}
                      name="testDate"
                      onChange={(value) => {
                        setFieldValue('testDate', value);
                      }}
                      prefix="Test"
                      value={data?.testDate}
                      max={true}
                    />
                    <FormikFhirDateField
                      formats={['datetime', 'date', 'year']}
                      label={formatMessage(messages.reported)}
                      name="reportedDate"
                      onChange={(value) => {
                        setFieldValue('reportedDate', value);
                      }}
                      prefix="Reported"
                      value={data?.reportedDate}
                      max={true}
                    />
                  </>
                ) : (
                  [
                    formatDateLabel(
                      messages.test,
                      typeof data?.getTestDateStr === 'function'
                        ? data.getTestDateStr()
                        : data?.testDate
                    ),
                    formatDateLabel(
                      messages.reported,
                      typeof data?.getReportedDateStr === 'function'
                        ? data.getReportedDateStr()
                        : data?.reportedDate
                    ),
                  ]
                )
              }
              title={title}
            >
              {/* <ColumnLayout>
                <DrilldownItem
                  label={formatMessage(messages.testName)}
                  name="title"
                  value={data?.reportTestName}
                />
              </ColumnLayout> */}

              <ColumnLayout>
                <DrilldownItem
                  label={formatMessage(messages.typeName)}
                  name="code"
                  onChange={(value) => setFieldValue('code', value)}
                  options={LAB_REPORT_TYPES}
                  type="dropdown"
                  value={data?.typeName}
                />
              </ColumnLayout>

              <HorizontalRule />

              <Title color={palette.text.primary} fontSize={17}>
                {formatMessage(messages.results)}
              </Title>
              {!values.code && (
                <Title color={palette.text.secondary} fontSize={14}>
                  {formatMessage(messages.noLabObservation)}
                </Title>
              )}
              {(isCreating || isEditing) && values.code ? (
                <FieldArray
                  name="results"
                  render={(arrayHelpers) => {
                    const labObservation: LabObservationValues = {
                      id: '',
                      // Lab report code
                      category: values.code ?? '',
                      // Lab observation code
                      code: '',
                      displayName: '',
                      testDate: values.testDate,
                      unit: '',
                      value: '',
                    };

                    if (!values.results?.length) {
                      arrayHelpers.push({ ...labObservation });
                    }

                    return (
                      <>
                        {values.results?.map((result, index) => (
                          <LabObservationForm
                            arrayHelpers={arrayHelpers}
                            code={values.code}
                            index={index}
                            key={`lab-observation-${index}`}
                            value={result}
                          />
                        ))}
                        <RoundedButton
                          autoSize
                          fontSize={13}
                          height={40}
                          iconName="plus"
                          onClick={() =>
                            arrayHelpers.push({ ...labObservation })
                          }
                          type="button"
                        >
                          {formatMessage(messages.addResult)}
                        </RoundedButton>
                      </>
                    );
                  }}
                />
              ) : (
                (data?.results ?? []).map(
                  ({ code, displayName, id, unit, value }) => (
                    <React.Fragment key={id}>
                      <DrilldownItem
                        label={displayName ?? code}
                        name="results"
                        readOnly
                        value={
                          // eslint-disable-next-line no-nested-ternary
                          isBooleanValue(value)
                            ? value === 'true' || value === true
                              ? 'Positive'
                              : 'Negative'
                            : `${value} ${unit ?? ''}`
                        }
                      />
                    </React.Fragment>
                  )
                )
              )}

              <HorizontalRule />
              <ColumnLayout>
                <DrilldownItem
                  label={formatMessage(messages.conclusion)}
                  name="details"
                  type="text-area"
                  value={data?.details}
                />
              </ColumnLayout>

              <RelatedRecordsForm associations={values.associatedResources} />

              <RecordAttachmentForm
                isCreating={isCreating}
                onFileChange={(files: DroppedFile[]) => {
                  setFieldValue('files', files);
                }}
                patientId={patient_id}
                record={{
                  attachments: data?.attachments,
                  resourceId: data?.getResourceId(),
                }}
              />

              <HorizontalRule />
              <RecordSubmitButton />
            </DrilldownWrapper>
          </Form>
        );
      }}
    </Formik>
  );
};

export default LabResultDrilldown;
